import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "../../utils/firebase";
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc, setDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import 'swiper/css';
import 'bootstrap/dist/css/bootstrap.min.css';

const PlacesSwiper = ({ isProfilePage = false }) => {
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const navigate = useNavigate();
  
  const categories = {
    "Bridges & Dams": [
      { name: "Tulay ng Zapote", location: "Brgy. Zapote II", type: "Immovable Tangible – Public Property" },
      { name: "Molino Dam", location: "Brgy. Molino III", type: "Immovable Tangible – Public Property" },
      { name: "Prinza Dam", location: "Brgy. San Nicolas I", type: "Immovable Tangible – Public Property" },
    ],
    "Monuments & Statues": [
      { name: "Fr. Mariano Gomes Monument", location: "Brgy. Poblacion", type: "Immovable Tangible – Public Property" },
      { name: "Monument of Love", location: "Brgy. Habay I", type: "Immovable Tangible – Private Property" },
      { name: "General Edilberto Evangelista Monument", location: "Brgy. Zapote II", type: "Immovable Tangible – Public Property" },
      { name: "Ginintuang Kasaysayan ng Lungsod ng Bacoor", location: "Brgy. Bayanan", type: "Immovable Tangible – Public Property" },
      { name: "St. Ezekiel Moreno Park", location: "Brgy. San Nicolas I", type: "Immovable Tangible – Public Property" },
    ],
    "Ancestral Houses": [
      { name: "Bahay ni Tisa", location: "Brgy. Kaingin Digman", type: "Immovable Tangible – Private Property" },
      { name: "Justice Buenaventura A. Ocampo Ancestral House", location: "Brgy. Kaingin Digman", type: "Immovable Tangible – Private Property" },
    ],
    "Churches & Religious Sites": [
      { name: "St. Michael the Archangel Church", location: "Brgy. Poblacion", type: "Immovable Tangible – Private Property" },
      { name: "Bacoor Unida Evangelical Church", location: "Brgy. Sinbanali", type: "Immovable Tangible – Private Property" },
      { name: "Sto. Niño de Molino Parish Church", location: "Brgy. Molino V", type: "Immovable Tangible – Private Property" },
    ],
    "Educational Institution": [
      { name: "Cavite State University - Bacoor Campus", location: "Soldiers Hills IV", type: "Educational Institution – Public Property" }
    ],
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchFavorites(currentUser.uid);
      }
    });
    return unsubscribe;
  }, []);

  const fetchFavorites = async (userId) => {
    const userDocRef = doc(db, "Users", userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      setFavorites(userDoc.data().favorites || {});
    } else {
      await setDoc(userDocRef, { favorites: {} });
      setFavorites({});
    }
  };

  const formatPlaceName = (placeName) => placeName.replace(/\s+/g, '').toLowerCase();

  const fetchImageUrl = async (placeName) => {
    const formattedName = formatPlaceName(placeName);
    const imageRef = ref(storage, `places/${formattedName}.jpg`);

    try {
      const url = await getDownloadURL(imageRef);
      setImageUrls((prevUrls) => ({ ...prevUrls, [placeName]: url }));
    } catch (error) {
      console.error("Error fetching image: ", error);
    }
  };

  useEffect(() => {
    Object.values(categories).flat().forEach((item) => fetchImageUrl(item.name));
  }, []);

  const toggleFavorite = async (event, category, item) => {
    event.stopPropagation(); // Prevent card click
    if (!user) return;

    const userDocRef = doc(db, "Users", user.uid);
    const updatedFavorites = { ...favorites };

    const isFavorited = updatedFavorites[category]?.includes(item.name);

    if (isFavorited) {
      updatedFavorites[category] = updatedFavorites[category].filter((name) => name !== item.name);
      await updateDoc(userDocRef, { [`favorites.${category}`]: arrayRemove(item.name) });
    } else {
      updatedFavorites[category] = [...(updatedFavorites[category] || []), item.name];
      await updateDoc(userDocRef, { [`favorites.${category}`]: arrayUnion(item.name) });
    }

    setFavorites(updatedFavorites);
  };

  const handleCardClick = (item) => {
    const routeMap = {
      "Tulay ng Zapote": "/tulayzapote",
      "Molino Dam": "/molinodam",
      "Prinza Dam": "/prinzadam",
      "Fr. Mariano Gomes Monument": "/marianogomes",
      "Monument of Love": "/monumentlove",
      "General Edilberto Evangelista Monument": "/edilbertomonument",
      "Ginintuang Kasaysayan ng Lungsod ng Bacoor": "/ginintuan",
      "Bahay ni Tisa": "/bahaytisa",
      "Justice Buenaventura A. Ocampo Ancestral House": "/justicehouse",
      "St. Michael the Archangel Church": "/michaelchurch",
      "Bacoor Unida Evangelical Church": "/unidachurch",
      "Sto. Niño de Molino Parish Church": "/stonino",
      "St. Ezekiel Moreno Park": "/stezekiel",
      "Cavite State University - Bacoor Campus": "/cvsu",
    };

    if (routeMap[item.name]) {
      navigate(routeMap[item.name]);
    }
  };

  return (
    <div>
      {Object.keys(categories).map((category, index) => (
        <div key={index} className="category-section">
          <h2>{category}</h2>
          <Swiper
            spaceBetween={10}
            slidesPerView={1}
            loop={true}
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            breakpoints={{
              768: { slidesPerView: 3 },
              1024: { slidesPerView: 4 },
            }}
            className="swiper-container"
          >
            {categories[category].map((item, index) => (
              <SwiperSlide key={index} className="swiper-slide">
                <div className="carousel-card position-relative" onClick={() => handleCardClick(item)}>
                  {user && !isProfilePage && (
                    <button
                      className={`btn btn-light position-absolute top-0 end-0 m-2 p-1 rounded-circle shadow ${
                        favorites[category]?.includes(item.name) ? "text-danger" : ""
                      }`}
                      onClick={(event) => toggleFavorite(event, category, item)}
                    >
                      <i className={`bi bi-heart${favorites[category]?.includes(item.name) ? "-fill" : ""}`}></i>
                    </button>
                  )}
                  <img
                    src={imageUrls[item.name] || `https://via.placeholder.com/300x200?text=${item.name}`}
                    alt={item.name}
                    className="carousel-image"
                  />
                  <div className="carousel-caption">
                    <h3>{item.name}</h3>
                    <p>{item.location}</p>
                    <small>{item.type}</small>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ))}
    </div>
  );
};

export default PlacesSwiper;
